import React from 'react';
import { SSEParser } from '../lib/streamParse.mjs';

/**
 * useAsk — drives POST /api/ask over SSE (spec §7.1).
 *
 * EventSource can't POST, so we stream with fetch + ReadableStream and parse the
 * named-event protocol (sources -> itinerary? -> token -> citations -> done | error)
 * manually. "itinerary" is a transit corpus's trip plan / departures card;
 * "replace" swaps the answer streamed so far for its text (a retracted reply).
 * Exposes incremental answer text, sources (for [n] chips), the validated cited
 * set, refusal/low-confidence flags, and a submit()/reset() API.
 */
export class AudienceRequiredError extends Error {
  constructor(audiences) {
    super('audience_required');
    this.name = 'AudienceRequiredError';
    this.code = 'audience_required';
    this.audiences = Array.isArray(audiences) ? audiences : [];
  }
}

/** The site refused this page: its origin is not on the site's allowed list,
 *  or the site id is unknown or inactive (/api/widget-token 403/404). No retry
 *  helps, so the thread says so instead of "Something went wrong". */
export class SiteNotEnabledError extends Error {
  constructor(status) {
    super('site_not_enabled');
    this.name = 'SiteNotEnabledError';
    this.code = 'site_not_enabled';
    this.status = status;
  }
}

export function useAsk({ backendUrl, siteId, signedToken, channelUrl, customerKey, agencyName, conversationId, audience = null }) {
  const [answer, setAnswer] = React.useState('');
  const [sources, setSources] = React.useState([]);
  const [itinerary, setItinerary] = React.useState(null);
  const [used, setUsed] = React.useState([]);
  const [done, setDone] = React.useState(null);
  const [status, setStatus] = React.useState('idle'); // idle | streaming | done | error
  const [error, setError] = React.useState(null);
  const [errorDetails, setErrorDetails] = React.useState(null);
  const [requestId, setRequestId] = React.useState(null);
  const abortRef = React.useRef(null);
  // One conversation id per widget session — groups queries for analytics.
  // Reuse a persisted id (24h memory restored from localStorage) so a resumed
  // conversation keeps the same grouping; otherwise mint a fresh one.
  const conversationIdRef = React.useRef(
    conversationId || ('c-' + Math.random().toString(36).slice(2) + Date.now().toString(36)));

  if (conversationId) conversationIdRef.current = conversationId;

  React.useEffect(() => () => abortRef.current?.abort(), []);

  const reset = React.useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setAnswer(''); setSources([]); setItinerary(null); setUsed([]); setDone(null);
    setStatus('idle'); setError(null); setErrorDetails(null); setRequestId(null);
  }, []);

  // Mint a fresh, origin-scoped widget token via POST (POST sends the Origin
  // header even same-origin; GET does not). Called per-ask so an expired
  // page-load token (15-min TTL) can never cause a 401 mid-conversation.
  const mintToken = React.useCallback(async () => {
    const r = await fetch(`${backendUrl}/api/widget-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site_id: siteId }),
    });
    if (r.status === 403 || r.status === 404) throw new SiteNotEnabledError(r.status);
    if (!r.ok) throw new Error('token ' + r.status);
    return (await r.json()).token;
  }, [backendUrl, siteId]);

  const submit = React.useCallback(async (question, history = []) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setAnswer(''); setSources([]); setItinerary(null); setUsed([]); setDone(null); setError(null);
    setErrorDetails(null);
    setStatus('streaming');

    const body = { question, history, conversation_id: conversationIdRef.current, audience };
    if (agencyName) body.agency_name = agencyName;

    try {
      // Prefer a fresh per-ask token when we can mint one (siteId embed); fall
      // back to a static signedToken, then customer_key/channel_url. A site
      // that refuses this page with nothing to fall back on is reported as
      // such: the ask would only fail anyway.
      let token = signedToken;
      if (siteId) {
        try {
          token = await mintToken();
        } catch (e) {
          if (e instanceof SiteNotEnabledError && !signedToken && !customerKey) throw e;
        }
      }
      if (token) body.signed_token = token;
      else { body.customer_key = customerKey; body.channel_url = channelUrl; }

      const resp = await fetch(`${backendUrl}/api/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) {
        const detail = await resp.json().catch(() => ({}));
        if (resp.status === 400 && detail.error === 'audience_required') {
          throw new AudienceRequiredError(detail.audiences);
        }
        throw new Error(detail.error || `HTTP ${resp.status}`);
      }

      if (controller.signal.aborted) return;
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      const parser = new SSEParser();

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done: streamDone } = await reader.read();
        if (controller.signal.aborted) return;
        if (streamDone) break;
        for (const { event, data } of parser.feed(decoder.decode(value, { stream: true }))) {
          if (event === 'sources') setSources(data);
          else if (event === 'itinerary') setItinerary(data);
          else if (event === 'token') setAnswer((a) => a + (data.t || ''));
          // The server retracts text it already streamed (an explanation that
          // ended in a refusal) and sends what replaces it.
          else if (event === 'replace') setAnswer(data.t || '');
          else if (event === 'citations') setUsed(data.used || []);
          else if (event === 'done') {
            setDone(data);
            setRequestId(data.request_id || null);
            setStatus('done');
          } else if (event === 'error') {
            setError(data.message || 'error');
          }
        }
      }
      setStatus((s) => (s === 'streaming' ? 'done' : s));
    } catch (e) {
      if (e.name === 'AbortError' || controller.signal.aborted) return;
      setErrorDetails(e);
      setError(e.message || 'request failed');
      setStatus('error');
    }
  }, [backendUrl, siteId, signedToken, channelUrl, customerKey, agencyName, mintToken, audience]);

  // requestIdOverride lets the thread UI vote on any past answer (each frozen
  // message carries its own requestId), not just the most recent one.
  const sendFeedback = React.useCallback(async (vote, comment = '', requestIdOverride = null) => {
    const rid = requestIdOverride || requestId;
    if (!rid) return;
    const body = { request_id: rid, vote, comment };
    let token = signedToken;
    if (siteId) { try { token = await mintToken(); } catch (e) { /* fall back */ } }
    if (token) body.signed_token = token;
    else { body.customer_key = customerKey; body.channel_url = channelUrl; }
    try {
      await fetch(`${backendUrl}/api/ask/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch { /* feedback is best-effort */ }
  }, [backendUrl, requestId, siteId, signedToken, channelUrl, customerKey, mintToken]);

  return { answer, sources, itinerary, used, done, status, error, errorDetails, requestId, submit, reset,
           sendFeedback };
}
